import React, { useEffect, useMemo, useState } from "react";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import { baseUrl } from "../../hooks/useFetch";

interface ColumnDef {
  dataField: string;
  caption: string;
  alignment?: string;
  format?: string;
}

interface MarketRiskStressTestsProps {
  cobDate?: Date | null;
  cols?: string[];
}

export default function MarketRiskStressTests({ cobDate, cols }: MarketRiskStressTestsProps) {
  const [rowData, setRowData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const allColumns = useMemo<ColumnDef[]>(
    () => [
      { dataField: "stress_category", caption: "Stress Category" },
      { dataField: "stress_name", caption: "Stress Name" },
      { dataField: "start_date", caption: "Start Date" },
      { dataField: "end_date", caption: "End Date" },
      { dataField: "pnl", caption: "P&L Impact (€'000s)", alignment: "right", format: "#,##0" },
    ],
    []
  );

  const visibleColumns = useMemo(() => {
    if (!Array.isArray(cols) || cols.length === 0) return allColumns;
    const wanted = new Set(cols);
    return allColumns.filter((c) => wanted.has(c.dataField));
  }, [cols, allColumns]);

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    const load = async () => {
      if (!cobDate) return;
      setLoading(true);

      try {
        const formattedSelectedDate = cobDate.toISOString().split("T")[0];
        const res = await fetch(
          `${baseUrl}/marketrisk_stress_tests?cob_date=${formattedSelectedDate}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setRowData(json.dash_rows || []);
      } catch (e: any) {
        if (!cancelled) setRowData([]);
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [cobDate]);

  return (
    <div>
      <DataGrid
        className="compact-grid"
        dataSource={rowData}
        showBorders
        columnAutoWidth
        wordWrapEnabled
        columnResizingMode="widget"
        loadPanel={{ enabled: true, showPane: true, text: "Loading...", shading: false }}

        onCellPrepared={(e: any) => {
              if (e.rowType !== "data") return;
              if (e.column?.dataField !== "pnl") return;

              const v = e.value;
              if (typeof v !== "number") return;

              const scaled = Math.abs(v) / 1000;
              const formatted = scaled.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              });

              e.cellElement.textContent = v < 0 ? `(${formatted})` : formatted;

              if (v < 0) {
                e.cellElement.style.color = "#d9534f";
                e.cellElement.style.fontWeight = "600";
              }
            }}



      >
        <Paging defaultPageSize={10} />

        {visibleColumns.map((c) => (
          <Column
            key={c.dataField}
            dataField={c.dataField}
            caption={c.caption}
            alignment={c.alignment as any}
            format={c.format}
          />
        ))}
      </DataGrid>

      {loading ? <div style={{ opacity: 0.7 }}>Loading…</div> : null}
    </div>
  );
}
